/**
 * The client's user-defined calculated rows — the DEFINITIONS, not their values.
 *
 * Loaded once per page rather than per scope: a custom metric belongs to the
 * client, not to a property, so switching tabs does not change this list. Its
 * one job on screen is to turn a grid row back into the id a delete addresses
 * — the table payload carries the row's key, and only this list knows the id
 * behind it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	type KpiCustomMetricSaveOutcome,
	type KpiCustomMetricDeleteOutcome,
	type KpiCustomMetricInput,
	kpiCustomMetricsService,
} from "../../services/kpiCustomMetricsService";
import type { KpiCustomMetric } from "../../types/kpi";

export interface KpiCustomMetricsData {
	metrics: KpiCustomMetric[];
	/** The id a `DELETE` takes for a grid row's key, or null if unknown. */
	idForKey: (metricKey: string) => string | null;
	/** Define a row. The caller refetches the table on success. */
	create: (input: KpiCustomMetricInput) => Promise<KpiCustomMetricSaveOutcome>;
	/** Change a row's label, expression or unit — its key stays put. */
	update: (
		id: string,
		input: KpiCustomMetricInput,
	) => Promise<KpiCustomMetricSaveOutcome>;
	/** Remove a row. The caller refetches the table on success. */
	remove: (id: string) => Promise<KpiCustomMetricDeleteOutcome>;
}

export function useKpiCustomMetrics(): KpiCustomMetricsData {
	const [metrics, setMetrics] = useState<KpiCustomMetric[]>([]);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Rising counter, so the initial list cannot land after the reload a create
	// or delete triggered and restore the snapshot that mutation replaced —
	// which would resurrect a deleted row's delete affordance.
	const requestId = useRef(0);

	const load = useCallback(async () => {
		const id = ++requestId.current;
		const next = await kpiCustomMetricsService.list();
		if (!mounted.current || id !== requestId.current) return;
		// A failed list is not an error state on this page: the grid still renders
		// every custom row the table carries. All that is lost is the delete
		// affordance, which is exactly what an unknown id can honestly offer.
		if (next !== null) setMetrics(next);
	}, []);

	// Same guard as useKpiTable's mount fetch: StrictMode's dev remount re-runs
	// this effect with the SAME `load`, and without it every mount pays a
	// duplicate custom-metrics fetch. `load` is stable for the hook's lifetime,
	// so this fires exactly once; mutations refetch through `load` directly.
	const lastAutoLoadRef = useRef<typeof load | null>(null);
	useEffect(() => {
		if (lastAutoLoadRef.current === load) return;
		lastAutoLoadRef.current = load;
		void load();
	}, [load]);

	const idByKey = useMemo(() => {
		const map = new Map<string, string>();
		metrics.forEach((metric) => map.set(metric.key, metric.id));
		return map;
	}, [metrics]);

	const idForKey = useCallback(
		(metricKey: string) => idByKey.get(metricKey) ?? null,
		[idByKey],
	);

	const create = useCallback(
		async (input: KpiCustomMetricInput) => {
			const outcome = await kpiCustomMetricsService.create(input);
			// Reload on a FAILED create too, not just a successful one: a request
			// that timed out after the server committed, or a 2xx whose body did
			// not match the contract, may have created the row. Retrying blind on
			// top of that is how a definition gets made twice — so the list the
			// user is about to retry against is refetched first.
			if (outcome.kind !== "invalid") await load();
			return outcome;
		},
		[load],
	);

	const update = useCallback(
		async (id: string, input: KpiCustomMetricInput) => {
			const outcome = await kpiCustomMetricsService.update(id, input);
			// Same reasoning as `create`: a save whose response was lost may have
			// landed, so the list the user is about to retry against is refetched
			// first. Only a rejected expression is unambiguous — nothing changed.
			if (outcome.kind !== "invalid") await load();
			return outcome;
		},
		[load],
	);

	const remove = useCallback(
		async (id: string) => {
			const outcome = await kpiCustomMetricsService.remove(id);
			// Reload either way, for the reason `create` does: a DELETE that lost
			// its response may still have committed, and the list is what decides
			// whether the row still has a delete to offer.
			await load();
			return outcome;
		},
		[load],
	);

	return useMemo(
		() => ({ metrics, idForKey, create, update, remove }),
		[metrics, idForKey, create, update, remove],
	);
}
